import { ErrorBoundary, LoadingSpinner } from "@rc-acs/shared/ui";
import type { QueryClient } from "@tanstack/react-query";
import { lazy, type ReactNode, Suspense } from "react";
import { createMemoryRouter } from "react-router-dom";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { CoreLayout } from "@/components/layout/CoreLayout";

const LoginPage = lazy(() => import("@/pages/Login").then((m) => ({ default: m.LoginPage })));
const NocDashboard = lazy(() => import("@/pages/NocDashboard").then((m) => ({ default: m.NocDashboard })));
const EngineRegistry = lazy(() => import("@/pages/EngineRegistry").then((m) => ({ default: m.EngineRegistry })));
const EngineDetail = lazy(() => import("@/pages/EngineDetail").then((m) => ({ default: m.EngineDetail })));
let billingModulePromise: Promise<typeof import("@/pages/Billing")> | null = null;
const getBillingModule = () => (billingModulePromise ??= import("@/pages/Billing"));
const Billing = lazy(() => getBillingModule().then((m) => ({ default: m.Billing })));
const BillingOverview = lazy(() => getBillingModule().then((m) => ({ default: m.BillingOverview })));
const ClientsPage = lazy(() => import("@/pages/ClientsPage").then((m) => ({ default: m.ClientsPage })));
const BillingPlansPage = lazy(() => import("@/pages/BillingPlansPage").then((m) => ({ default: m.BillingPlansPage })));
const ContractsPage = lazy(() => import("@/pages/ContractsPage").then((m) => ({ default: m.ContractsPage })));
const VirtualParams = lazy(() => import("@/pages/VirtualParams").then((m) => ({ default: m.VirtualParams })));
const Provisions = lazy(() => import("@/pages/Provisions").then((m) => ({ default: m.Provisions })));
const Presets = lazy(() => import("@/pages/Presets").then((m) => ({ default: m.Presets })));
const DynamicVars = lazy(() => import("@/pages/DynamicVars").then((m) => ({ default: m.DynamicVars })));
const EngineeringSyncStatus = lazy(() => import("@/pages/EngineeringSyncStatus").then((m) => ({ default: m.EngineeringSyncStatus })));
const CpeModels = lazy(() => import("@/pages/CpeModels").then((m) => ({ default: m.CpeModels })));
const VendorMappings = lazy(() => import("@/pages/VendorMappings").then((m) => ({ default: m.VendorMappings })));
const Firmware = lazy(() => import("@/pages/Firmware").then((m) => ({ default: m.Firmware })));
const Files = lazy(() => import("@/pages/Files").then((m) => ({ default: m.Files })));
const Users = lazy(() => import("@/pages/Users").then((m) => ({ default: m.Users })));
const Backups = lazy(() => import("@/pages/Backups").then((m) => ({ default: m.Backups })));
const Settings = lazy(() => import("@/pages/Settings").then((m) => ({ default: m.Settings })));
const CwmpConfigs = lazy(() => import("@/pages/CwmpConfigs").then((m) => ({ default: m.CwmpConfigs })));
const Tr369Placeholder = lazy(() => import("@/pages/Tr369Placeholder").then((m) => ({ default: m.Tr369Placeholder })));
const Olts = lazy(() => import("@/pages/Olts").then((m) => ({ default: m.Olts })));
const NotFound = lazy(() => import("@/pages/NotFound").then((m) => ({ default: m.NotFound })));

function lazyBoundary(element: ReactNode) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingSpinner className="h-full min-h-[200px]" />}>{element}</Suspense>
    </ErrorBoundary>
  );
}

export function createAppRouter(_queryClient: QueryClient, initialEntries?: string[]) {
  return createMemoryRouter([
    {
      path: "/login",
      element: lazyBoundary(<LoginPage />),
    },
    {
      path: "/",
      element: <ProtectedRoute />,
      loader: ProtectedRoute.loader,
      hydrateFallbackElement: (
        <div className="flex h-screen items-center justify-center">
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      ),
      children: [
        {
          element: <CoreLayout />,
          children: [
            { index: true, element: lazyBoundary(<NocDashboard />) },
            { path: "engines", element: lazyBoundary(<EngineRegistry />) },
            { path: "engines/:uuid", element: lazyBoundary(<EngineDetail />) },
            {
              path: "engineering",
              children: [
                // TR-069 (GenieACS)
                { path: "tr069/presets", element: lazyBoundary(<Presets />) },
                { path: "tr069/provisions", element: lazyBoundary(<Provisions />) },
                { path: "tr069/virtual-params", element: lazyBoundary(<VirtualParams />) },
                { path: "tr069/cwmp-configs", element: lazyBoundary(<CwmpConfigs />) },
                { path: "tr069/files", element: lazyBoundary(<Files />) },
                // TR-369 (USP/OpenUSP) — placeholder
                { path: "tr369/controllers", element: lazyBoundary(<Tr369Placeholder />) },
                // Devices (CPE catalog)
                { path: "devices/cpe-models", element: lazyBoundary(<CpeModels />) },
                { path: "devices/vendor-mappings", element: lazyBoundary(<VendorMappings />) },
                { path: "devices/firmware", element: lazyBoundary(<Firmware />) },
                // OLT
                { path: "olt", element: lazyBoundary(<Olts />) },
                // Dynamic Vars (transversal)
                { path: "dynamic-vars", element: lazyBoundary(<DynamicVars />) },
                { path: "sync-status", element: lazyBoundary(<EngineeringSyncStatus />) },
              ],
            },
            {
              path: "admin",
              children: [
                { path: "users", element: lazyBoundary(<Users />) },
                { path: "settings", element: lazyBoundary(<Settings />) },
                { path: "backups", element: lazyBoundary(<Backups />) },
                {
                  path: "billing",
                  element: lazyBoundary(<Billing />),
                  children: [
                    { index: true, element: lazyBoundary(<BillingOverview />) },
                    { path: "clients", element: lazyBoundary(<ClientsPage />) },
                    { path: "plans", element: lazyBoundary(<BillingPlansPage />) },
                    { path: "contracts", element: lazyBoundary(<ContractsPage />) },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: "*",
      element: lazyBoundary(<NotFound />),
    },
  ], {
    initialEntries: initialEntries ?? ["/"],
  });
}
